import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { ANEP_CLIENT_ID } from '../../types/configTemplate';

interface Props {
  /** ANEP_CLIENT_ID for an ANEP template, a client UUID otherwise, '' for nothing picked yet. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: boolean;
  id?: string;
}

/**
 * Who owns the template: ANEP, or one client. Schema v2 has one owner per
 * template — an ANEP template serves every client, a client template only
 * that client.
 *
 * ANEP staff choose freely. A client's admin only sees their own client(s):
 * with a single client the choice is made for them and the control is
 * read-only.
 */
export const TemplateOwnerSelect: React.FC<Props> = ({ value, onChange, disabled, error, id }) => {
  const { t } = useI18n();
  const { isAnepStaff, allowedClientIds } = useClientAccess();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    clientService
      .fetchActiveClients()
      .then((list) => {
        if (!cancelled) setClients(list);
      })
      .catch((err) => {
        if (!cancelled) console.error('[TEMPLATE OWNER] fetch clients:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A client's admin may only pick among their own clients.
  const choices = useMemo(
    () => (isAnepStaff ? clients : clients.filter((c) => allowedClientIds.includes(c.id))),
    [clients, isAnepStaff, allowedClientIds],
  );

  // One client, no ANEP: nothing to choose — settle it.
  const locked = !isAnepStaff && allowedClientIds.length === 1;
  useEffect(() => {
    if (locked && value !== allowedClientIds[0]) onChange(allowedClientIds[0]);
  }, [locked, allowedClientIds, value, onChange]);

  return (
    <select
      id={id}
      className={error ? 'error' : ''}
      value={value}
      disabled={disabled || loading || locked}
      onChange={(e) => onChange(e.target.value)}
      data-testid="field-template-owner"
    >
      {loading ? (
        <option value="">{t('configTemplates.ownerLoadingClients')}</option>
      ) : (
        <>
          {!locked && <option value="">{t('configTemplates.ownerPickClient')}</option>}
          {isAnepStaff && <option value={ANEP_CLIENT_ID}>{t('configTemplates.ownerAnep')}</option>}
          {choices.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </>
      )}
    </select>
  );
};

export default TemplateOwnerSelect;
